import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Inline SVG Logo (preferred for better control)
const Logo = () => (
    <svg width="140" height="40" viewBox="0 0 240 60" xmlns="http://www.w3.org/2000/svg">
        <path d="M20,40 C30,30 40,50 50,40 C60,30 70,50 80,40"
            stroke="#4338CA"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round" />
        <g transform="translate(10, 25) scale(0.7)">
            <circle cx="10" cy="30" r="3" fill="#4338CA" />
            <circle cx="24" cy="30" r="3" fill="#4338CA" />
            <path d="M0,0 L5,0 L8,20 L26,20 L30,5 L10,5"
                stroke="#4338CA"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round" />
        </g>
        <text x="90" y="35"
            fontFamily="'Poppins', sans-serif"
            fontSize="24"
            fontWeight="bold"
            fill="#4338CA">
            Shop<tspan fill="#8B5CF6">Wave</tspan>
        </text>
        <text x="90" y="50"
            fontFamily="'Open Sans', sans-serif"
            fontSize="10"
            fill="#6B7280">
            電商浪潮的推手
        </text>
    </svg>
);

const NavBar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: var(--bg-white);
  box-shadow: var(--shadow-sm);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all var(--transition-normal);
  
  &.scrolled {
    padding: 0.5rem 2rem;
    box-shadow: var(--shadow-md);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    
    &.scrolled {
      padding: 0.5rem 1rem;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 250px;
    flex-direction: column;
    background-color: var(--bg-white);
    box-shadow: var(--shadow-xl);
    padding: 2rem;
    transform: translateX(${props => props.$isOpen ? '0' : '100%'});
    transition: transform var(--transition-normal);
    z-index: 1001;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-dark);
  margin-left: 2rem;
  font-weight: 500;
  transition: color var(--transition-fast);
  
  &:hover {
    color: var(--primary);
  }
  
  @media (max-width: 768px) {
    margin: 1rem 0;
  }
`;

const ConsultButton = styled(Link)`
  background-color: var(--primary);
  color: white;
  padding: 0.5rem 1.25rem;
  border-radius: var(--radius-md);
  margin-left: 2rem;
  font-weight: 600;
  transition: background-color var(--transition-fast);
  
  &:hover {
    background-color: var(--primary-dark);
    color: white;
  }
  
  @media (max-width: 768px) {
    margin: 1.5rem 0 0 0;
  }
`;

const Hamburger = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1002;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Overlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
`;

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Handle scroll event
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        // Clean up
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    return (
        <>
            <NavBar className={isScrolled ? 'scrolled' : ''}>
                <Link to="/" onClick={closeMenu}>
                    <Logo />
                </Link>

                <Hamburger onClick={toggleMenu}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {isMenuOpen ? (
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        ) : (
                            <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        )}
                    </svg>
                </Hamburger>

                <NavLinks $isOpen={isMenuOpen}>
                    <NavLink to="/features" onClick={closeMenu}>功能</NavLink>
                    <NavLink to="/#how-it-works" onClick={closeMenu}>運作方式</NavLink>
                    <NavLink to="/pricing" onClick={closeMenu}>方案與定價</NavLink>
                    <ConsultButton to="/contact" onClick={closeMenu}>免費諮詢</ConsultButton>
                </NavLinks>
            </NavBar>

            <Overlay $isOpen={isMenuOpen} onClick={closeMenu} />
        </>
    );
};

export default Navbar;